import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from './AppContext';
import { getCurrentUser, updateSelf } from '@/api/services/userService';
import type { UserResponseApi } from '@/api/models/user';

// ── Types ─────────────────────────────────────────────────────────────────────

export type OnboardingStepId =
  | 'general-settings'
  | 'data-sources'
  | 'roles-permissions'
  | 'user-management'
  | 'dashboard-layout';

export type StepStatus = 'upcoming' | 'active' | 'completed' | 'skipped';

export interface OnboardingStep {
  id: OnboardingStepId;
  number: number;
  name: string;
  sidebarDescription: string;
  cardDescription: string;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'general-settings',
    number: 1,
    name: 'General Settings',
    sidebarDescription: 'Configure company & forecast defaults',
    cardDescription: 'Configure your company, financial year and forecast defaults',
  },
  {
    id: 'data-sources',
    number: 2,
    name: 'Data Sources',
    sidebarDescription: 'Connect your data sources',
    cardDescription: 'Connect your accounting, payroll and banking systems',
  },
  {
    id: 'roles-permissions',
    number: 3,
    name: 'Roles & Permissions',
    sidebarDescription: 'Configure roles for your team',
    cardDescription: 'Set up and customise roles before assigning users',
  },
  {
    id: 'user-management',
    number: 4,
    name: 'User Management',
    sidebarDescription: 'Invite your team',
    cardDescription: 'Invite your team and assign roles',
  },
  {
    id: 'dashboard-layout',
    number: 5,
    name: 'Dashboard Layout',
    sidebarDescription: 'Set default dashboard layouts',
    cardDescription: 'Configure default card layouts for each role',
  },
];

// localStorage key helpers
const completedKey = (userId: string) => `onboarding_completed_${userId}`;
const savedStepKey = (userId: string) => `onboarding_savedstep_${userId}`;
const skippedKey = (userId: string) => `onboarding_skipped_${userId}`;

// ── Context shape ──────────────────────────────────────────────────────────────

interface OnboardingContextValue {
  // State
  isAssistantOpen: boolean;
  hasCompleted: boolean;
  hasDismissed: boolean;
  currentStep: number; // 0 = Welcome, 1-4 = steps, 5 = Completion
  skippedSteps: Set<OnboardingStepId>;
  // Actions
  openAssistant: () => void;
  dismissOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipCurrentStep: () => void;
  getStepStatus: (stepId: OnboardingStepId) => StepStatus;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, mode } = useApp();
  const userId = currentUser.id;
  const isLive = mode === 'live';
  const queryClient = useQueryClient();

  // Has the user permanently completed onboarding?
  // In live mode, default to true (assume completed until the API says otherwise).
  // This prevents the OnboardingGate from flashing open before the API response
  // arrives — the sync effect will set it to false if the user hasn't completed.
  const [hasCompleted, setHasCompleted] = useState<boolean>(() => {
    if (!isLive) {
      try {
        return localStorage.getItem(completedKey(userId)) === 'true';
      } catch {
        return false;
      }
    }
    return true;
  });

  // Has the user dismissed (skipped) this session? (React state only — resets on refresh)
  const [hasDismissed, setHasDismissed] = useState(false);

  // Current step
  const [currentStep, setCurrentStep] = useState<number>(() => {
    if (!isLive) {
      try {
        const saved = localStorage.getItem(savedStepKey(userId));
        return saved ? Number.parseInt(saved, 10) : 0;
      } catch {
        return 0;
      }
    }
    return 0;
  });

  // Which steps has the user skipped?
  const [skippedSteps, setSkippedSteps] = useState<Set<OnboardingStepId>>(() => {
    if (!isLive) {
      try {
        const raw = localStorage.getItem(skippedKey(userId));
        return raw ? new Set(JSON.parse(raw) as OnboardingStepId[]) : new Set();
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  // Is the overlay open?
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  // Guard: suppress the OnboardingGate's auto-open while we are
  // transitioning into live mode. Refs update synchronously so the
  // child effect in OnboardingGate sees the new value in the same
  // React commit, avoiding the setState race between parent and child.
  const suppressAutoOpenRef = useRef(false);

  // LIVE MODE: Read onboarding state from the ['currentUser'] query.
  // Uses the same staleTime/refetchOnWindowFocus as UserPreferencesContext so
  // all observers agree on cache behaviour. The select function extracts only
  // the onboarding-relevant preferences so React only re-renders when those
  // values actually change.
  const { data: apiOnboardingState } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    enabled: isLive,
    staleTime: 0,
    refetchOnWindowFocus: true,
    select: (data: UserResponseApi) => ({
      onboardingCompleted: data?.preferences?.onboardingCompleted,
      onboardingSavedStep: data?.preferences?.onboardingSavedStep,
      onboardingSkippedSteps: data?.preferences?.onboardingSkippedSteps,
    }),
  });

  const [hydratedStepState, setHydratedStepState] = useState(false);

  useEffect(() => {
    if (!isLive || !apiOnboardingState) return;

    // We now have definitive API state — allow auto-open again.
    suppressAutoOpenRef.current = false;

    // Keep hasCompleted in sync with the API.
    // Treat a missing (undefined) value as "not completed" so that clearing the
    // database correctly resets onboarding state.
    const completed = apiOnboardingState.onboardingCompleted === 'true';
    setHasCompleted(completed);
    if (!completed) {
      setHasDismissed(false);
    }

    // Only hydrate step/skip state once (avoid overwriting in-progress onboarding)
    if (!hydratedStepState) {
      if (apiOnboardingState.onboardingSavedStep !== undefined) {
        const step = Number.parseInt(apiOnboardingState.onboardingSavedStep, 10);
        if (!Number.isNaN(step)) setCurrentStep(step);
      }
      if (apiOnboardingState.onboardingSkippedSteps !== undefined) {
        try {
          const parsed = JSON.parse(apiOnboardingState.onboardingSkippedSteps) as OnboardingStepId[];
          setSkippedSteps(new Set(parsed));
        } catch { /* ignore malformed JSON */ }
      }
      setHydratedStepState(true);
    }
  }, [isLive, apiOnboardingState, hydratedStepState]);

  // LIVE MODE: When switching INTO live mode, set safe defaults
  // to prevent the OnboardingGate from flashing open before the
  // API response arrives. The sync effect above will correct
  // hasCompleted once ['currentUser'] resolves.
  useEffect(() => {
    if (isLive) {
      suppressAutoOpenRef.current = true; // block gate until API responds
      setHasCompleted(true);       // assume completed until API says otherwise
      setHasDismissed(false);
      setIsAssistantOpen(false);   // close any open assistant from demo mode
      setHydratedStepState(false); // allow API hydration to run fresh
    }
  }, [isLive]);

  // DEMO MODE: Re-read localStorage when user changes (demo mode switcher)
  useEffect(() => {
    if (!isLive) {
      // Clear the live-mode suppress guard — demo mode has no API to wait for,
      // so openAssistant() should work immediately.
      suppressAutoOpenRef.current = false;
      try {
        const completed = localStorage.getItem(completedKey(userId)) === 'true';
        setHasCompleted(completed);
        const saved = localStorage.getItem(savedStepKey(userId));
        setCurrentStep(saved ? Number.parseInt(saved, 10) : 0);
        const raw = localStorage.getItem(skippedKey(userId));
        setSkippedSteps(raw ? new Set(JSON.parse(raw) as OnboardingStepId[]) : new Set());
        setHasDismissed(false);
        setIsAssistantOpen(false);
      } catch {
        // ignore
      }
    }
  }, [userId, isLive]);

  // DEMO MODE: Persist currentStep to localStorage whenever it changes
  useEffect(() => {
    if (!isLive) {
      try {
        localStorage.setItem(savedStepKey(userId), String(currentStep));
      } catch {
        // ignore
      }
    }
  }, [currentStep, userId, isLive]);

  // DEMO MODE: Persist skippedSteps to localStorage whenever it changes
  useEffect(() => {
    if (!isLive) {
      try {
        localStorage.setItem(skippedKey(userId), JSON.stringify([...skippedSteps]));
      } catch {
        // ignore
      }
    }
  }, [skippedSteps, userId, isLive]);

  const persistToApi = useCallback(
    (updates: Record<string, string>) => {
      if (!isLive) return;
      updateSelf(updates).then(() => {
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      }).catch(() => {
        // silently ignore API errors for onboarding persistence
      });
    },
    [isLive, queryClient],
  );

  const openAssistant = useCallback(() => {
    if (suppressAutoOpenRef.current) return;
    setIsAssistantOpen(true);
  }, []);

  const dismissOnboarding = useCallback(() => {
    if (isLive) {
      persistToApi({ onboardingCompleted: 'true' });
    } else {
      try {
        localStorage.setItem(completedKey(userId), 'true');
      } catch {
        // ignore
      }
    }
    setHasCompleted(true);
    setHasDismissed(true);
    setIsAssistantOpen(false);
  }, [isLive, persistToApi, userId]);

  const completeOnboarding = useCallback(() => {
    if (isLive) {
      persistToApi({ onboardingCompleted: 'true' });
    } else {
      try {
        localStorage.setItem(completedKey(userId), 'true');
      } catch {
        // ignore
      }
    }
    setHasCompleted(true);
    setIsAssistantOpen(false);
  }, [userId, isLive, persistToApi]);

  const resetOnboarding = useCallback(() => {
    if (isLive) {
      persistToApi({
        onboardingCompleted: 'false',
        onboardingSavedStep: '0',
        onboardingSkippedSteps: '[]',
      });
    } else {
      try {
        localStorage.removeItem(completedKey(userId));
        localStorage.removeItem(savedStepKey(userId));
        localStorage.removeItem(skippedKey(userId));
      } catch {
        // ignore
      }
    }
    setHasCompleted(false);
    setHasDismissed(false);
    setSkippedSteps(new Set());
    setCurrentStep(0);
    setIsAssistantOpen(true);
  }, [userId, isLive, persistToApi]);

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
      if (isLive) {
        persistToApi({ onboardingSavedStep: String(step) });
      }
    },
    [isLive, persistToApi],
  );

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = Math.min(prev + 1, ONBOARDING_STEPS.length + 1);
      if (isLive) {
        persistToApi({ onboardingSavedStep: String(next) });
      }
      return next;
    });
  }, [isLive, persistToApi]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const skipCurrentStep = useCallback(() => {
    const stepIndex = currentStep - 1;
    const step = ONBOARDING_STEPS[stepIndex];
    if (step) {
      setSkippedSteps((prev) => {
        const next = new Set(prev);
        next.add(step.id);
        if (isLive) {
          persistToApi({ onboardingSkippedSteps: JSON.stringify([...next]) });
        }
        return next;
      });
    }
    nextStep();
  }, [currentStep, nextStep, isLive, persistToApi]);

  const getStepStatus = useCallback(
    (stepId: OnboardingStepId): StepStatus => {
      const step = ONBOARDING_STEPS.find((s) => s.id === stepId);
      if (!step) return 'upcoming';
      const stepNumber = step.number;

      if (currentStep === stepNumber) return 'active';
      if (stepNumber < currentStep) {
        return skippedSteps.has(stepId) ? 'skipped' : 'completed';
      }
      return 'upcoming';
    },
    [currentStep, skippedSteps],
  );

  const value = useMemo(
    () => ({
      isAssistantOpen,
      hasCompleted,
      hasDismissed,
      currentStep,
      skippedSteps,
      openAssistant,
      dismissOnboarding,
      completeOnboarding,
      resetOnboarding,
      goToStep,
      nextStep,
      prevStep,
      skipCurrentStep,
      getStepStatus,
    }),
    [
      isAssistantOpen,
      hasCompleted,
      hasDismissed,
      currentStep,
      skippedSteps,
      openAssistant,
      dismissOnboarding,
      completeOnboarding,
      resetOnboarding,
      goToStep,
      nextStep,
      prevStep,
      skipCurrentStep,
      getStepStatus,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
};
